import {
  EscrowOfficerAction,
  EscrowOfficerActionType,
  GetEscrowOfficerDetailStartAction,
  GetEscrowOfficerDetailSuccessAction,
  GetEscrowOfficerDetailFailureAction,
  EscrowOfficerSearchInitAction,
  EscrowOfficerSearchFailureAction,
  EscrowOfficerSearchSuccessAction,
  GetContractsByOfficerStartAction,
  GetContractsByOfficerSuccessAction,
  GetContractsByOfficerFailureAction
} from './escrow-officer.actions';
import { createReducer } from '@common/redux-utils';
import { OfficerSearchViewModel, EscrowOfficerViewModel } from '@models/EscrowOfficer';
import { ContractSimpleViewModel } from '@models/Contract';

// Defining the state of the page.
// Should be one per page.
// All properties should be readonly.
export interface EscrowOfficerState {
  readonly errorMessage?: string;
  readonly saving?: boolean;
  readonly statusMessage?: string;
  readonly query?: string | null;
  readonly escrowOfficerLoading?: boolean;
  readonly escrowOfficerPageCount?: number;
  readonly escrowOfficerPageNumber?: number;
  readonly escrowOfficerPageSize?: number;
  readonly escrowOfficerOrderBy?: string;
  readonly escrowOfficers?: OfficerSearchViewModel[];
  readonly escrowOfficerVM: EscrowOfficerViewModel | null;
  readonly contracts: Array<ContractSimpleViewModel>;
}

// This is the initial state of the page
const initialState: EscrowOfficerState = {
  errorMessage: '',
  statusMessage: '',
  query: '',
  escrowOfficerLoading: false,
  escrowOfficerPageCount: 0,
  escrowOfficerPageNumber: 1,
  escrowOfficers: [],
  escrowOfficerPageSize: 10,
  escrowOfficerOrderBy: 'id',
  escrowOfficerVM: null,
  contracts: []
};

function handleGetEscrowOfficerStart(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    errorMessage: '',
    statusMessage: action.statusMessage
  };
}

function handleGetEscrowOfficerSuccess(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    statusMessage: action.statusMessage
  };
}

function handleGetEscrowOfficerFailure(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    errorMessage: action.errorMessage || '',
    statusMessage: action.statusMessage
  };
}

function handleGetEscrowOfficerDetailStart(
  state: EscrowOfficerState,
  action: GetEscrowOfficerDetailStartAction
): EscrowOfficerState {
  return {
    ...state,
    errorMessage: '',
    statusMessage: action.statusMessage
  };
}

function handleGetEscrowOfficerDetailSuccess(
  state: EscrowOfficerState,
  action: GetEscrowOfficerDetailSuccessAction
): EscrowOfficerState {
  return {
    ...state,
    escrowOfficerVM: action.escrowOfficerVM,
    statusMessage: action.statusMessage
  };
}

function handleGetEscrowOfficerDetailFailure(
  state: EscrowOfficerState,
  action: GetEscrowOfficerDetailFailureAction
): EscrowOfficerState {
  return {
    ...state,
    errorMessage: action.errorMessage || '',
    statusMessage: action.statusMessage
  };
}

function handleGetContractsByOfficerStart(
  state: EscrowOfficerState,
  action: GetContractsByOfficerStartAction
): EscrowOfficerState {
  return {
    ...state,
    errorMessage: '',
    statusMessage: action.statusMessage
  };
}

function handleGetContractsByOfficeSuccess(
  state: EscrowOfficerState,
  action: GetContractsByOfficerSuccessAction
): EscrowOfficerState {
  return {
    ...state,
    errorMessage: '',
    statusMessage: action.statusMessage,
    contracts: action.result.results
  };
}

function handleGetContractsByOfficeFailure(
  state: EscrowOfficerState,
  action: GetContractsByOfficerFailureAction
): EscrowOfficerState {
  return {
    ...state,
    errorMessage: action.errorMessage,
    statusMessage: action.statusMessage
  };
}

function handleCreateEscrowOfficerStart(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    saving: true,
    statusMessage: action.statusMessage
  };
}

function handleCreateEscrowOfficerSuccess(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    saving: false,
    statusMessage: action.statusMessage
  };
}

function handleCreateEscrowOfficerFailure(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    saving: false,
    errorMessage: action.errorMessage || '',
    statusMessage: action.statusMessage
  };
}

function handleEditEscrowOfficerStart(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    statusMessage: action.statusMessage
  };
}

function handleEditEscrowOfficerSuccess(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    statusMessage: action.statusMessage
  };
}

function handleEditEscrowOfficerFailure(state: EscrowOfficerState, action: EscrowOfficerAction): EscrowOfficerState {
  return {
    ...state,
    errorMessage: action.errorMessage || '',
    statusMessage: action.statusMessage
  };
}

/********** Search **********/
function handleEscrowOfficerSearchInit(
  state: EscrowOfficerState,
  action: EscrowOfficerSearchInitAction
): EscrowOfficerState {
  return {
    ...state,
    statusMessage: 'Search Init',
    query: action.query,
    escrowOfficerLoading: true
  };
}

function handleEscrowOfficerSearchFailure(
  state: EscrowOfficerState,
  action: EscrowOfficerSearchFailureAction
): EscrowOfficerState {
  return {
    ...state,
    statusMessage: action.errorMessage,
    escrowOfficerLoading: false
  };
}

function handleEscrowOfficerSearchSuccess(
  state: EscrowOfficerState,
  action: EscrowOfficerSearchSuccessAction
): EscrowOfficerState {
  return {
    ...state,
    statusMessage: 'Search Success!',
    escrowOfficerLoading: false,
    escrowOfficerPageCount: Math.ceil(action.result.totalResults / action.result.pageSize),
    escrowOfficerPageNumber: action.pageNumber,
    escrowOfficerPageSize: action.pageSize,
    escrowOfficerOrderBy: action.orderBy,
    escrowOfficers: action.result.results
  };
}

// This creates the reducer as redux wants it w/ a helper function
// There should an entry for each action type
export const EscrowOfficerReducer = createReducer(initialState, {
  [EscrowOfficerActionType.GetEscrowOfficerStart]: handleGetEscrowOfficerStart,
  [EscrowOfficerActionType.GetEscrowOfficerSuccess]: handleGetEscrowOfficerSuccess,
  [EscrowOfficerActionType.GetEscrowOfficerFailure]: handleGetEscrowOfficerFailure,

  [EscrowOfficerActionType.GetEscrowOfficerDetailStart]: handleGetEscrowOfficerDetailStart,
  [EscrowOfficerActionType.GetEscrowOfficerDetailSuccess]: handleGetEscrowOfficerDetailSuccess,
  [EscrowOfficerActionType.GetEscrowOfficerDetailFailure]: handleGetEscrowOfficerDetailFailure,

  [EscrowOfficerActionType.GetContractsByOfficerStart]: handleGetContractsByOfficerStart,
  [EscrowOfficerActionType.GetContractsByOfficerSuccess]: handleGetContractsByOfficeSuccess,
  [EscrowOfficerActionType.GetContractsByOfficerFailure]: handleGetContractsByOfficeFailure,

  [EscrowOfficerActionType.CreateEscrowOfficerStart]: handleCreateEscrowOfficerStart,
  [EscrowOfficerActionType.CreateEscrowOfficerSuccess]: handleCreateEscrowOfficerSuccess,
  [EscrowOfficerActionType.CreateEscrowOfficerFailure]: handleCreateEscrowOfficerFailure,

  [EscrowOfficerActionType.EditEscrowOfficerStart]: handleEditEscrowOfficerStart,
  [EscrowOfficerActionType.EditEscrowOfficerSuccess]: handleEditEscrowOfficerSuccess,
  [EscrowOfficerActionType.EditEscrowOfficerFailure]: handleEditEscrowOfficerFailure,

  /********** Search **********/
  [EscrowOfficerActionType.EscrowOfficerSearchInit]: handleEscrowOfficerSearchInit,
  [EscrowOfficerActionType.EscrowOfficerSearchFailure]: handleEscrowOfficerSearchFailure,
  [EscrowOfficerActionType.EscrowOfficerSearchSuccess]: handleEscrowOfficerSearchSuccess
});



// WEBPACK FOOTER //
// ./src/escrow-officer/escrow-officer.reducer.ts